import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { GzModalShell, GzButton, GzInput, GzFormField } from '@gozem/ui';
import { AccountStore, AccountLogoStore, MerchantStore } from '../../../core/stores';
import { AuthService } from '@core/services/auth.service';
import { ConfigService } from '@core/services';
import { ErrorHandler } from '@core/errors';
import { ErrorCode } from '@core/models';
import type { MerchantAccount } from '../../../core/models';

/**
 * AccountSwitcherComponent
 * Left-side topbar element displaying the active business account
 * with a searchable dropdown to switch between accounts.
 *
 * State ownership:
 *  - isOpen, searchQuery, filteredAccounts → local signals (UI only)
 *  - pendingAccount, password, submitting  → local signals (switch dialog)
 *  - accounts, activeAccount               → AccountStore (source of truth)
 *
 * Switching is a **re-authentication**, not a client-side swap: picking another account opens
 * a password dialog and, on submit, re-logs in against that account via
 * `AuthService.switchAccount` (POST /account/session). That installs a fresh, consistent
 * session for the target merchant; the app then hard-reloads to /wallet so every page-scoped
 * store rebuilds against it. This is the fix for the old optimistic swap that left Gozem Money
 * requests mismatched and failing with error 1032. See `AuthService.switchAccount`.
 */
@Component({
  selector: 'app-account-switcher',
  imports: [FormsModule, GzModalShell, GzButton, GzInput, GzFormField],
  templateUrl: './account-switcher.component.html',
  styleUrl: './account-switcher.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class AccountSwitcherComponent {
  private readonly authService = inject(AuthService);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountLogoStore = inject(AccountLogoStore);
  private readonly merchantStore = inject(MerchantStore);
  private readonly config = inject(ConfigService);
  protected readonly accountStore = inject(AccountStore);

  /** Controls open/closed state of the dropdown panel. */
  readonly isOpen = signal(false);

  /** Local search query — filters the account list inside the dropdown. */
  readonly searchQuery = signal('');

  // ── Switch dialog state ──────────────────────────────────────────────────────

  /** The account awaiting password confirmation, or `null` when the dialog is closed. */
  readonly pendingAccount = signal<MerchantAccount | null>(null);

  /** Password typed in the switch dialog. Held here only, never persisted. */
  readonly password = signal('');

  /** Whether the password is revealed (eye toggle). */
  readonly showPassword = signal(false);

  /** True while the re-authentication request is in flight. */
  readonly submitting = signal(false);

  /** Inline error shown inside the switch dialog (e.g. wrong password). */
  readonly dialogError = signal<string | null>(null);

  /** `accountId`s whose logo image failed to load — fall back to the coloured avatar. */
  private readonly brokenLogos = signal<ReadonlySet<string>>(new Set());

  /** The confirm button is live only when a password has been entered and nothing is in flight. */
  readonly canConfirm = computed(() => this.password().trim().length > 0 && !this.submitting());

  /**
   * Accounts filtered by the local searchQuery.
   *
   * The `?? ''` guards are a second line of defence behind `normalizeMerchantAccounts`
   * in AccountStore: a throw in here is cached by the computed and re-thrown on every
   * later read, which kills the whole dropdown until a full page reload.
   */
  readonly filteredAccounts = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const accounts = this.accountStore.accounts();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        (a.name ?? '').toLowerCase().includes(q) ||
        (a.email ?? '').toLowerCase().includes(q) ||
        (a.accountId ?? '').toLowerCase().includes(q),
    );
  });

  // ── Outside-click & keyboard handlers ──────────────────────────────────────

  /** Close the dropdown when the user clicks anywhere outside it. */
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('app-account-switcher')) {
      this.close();
    }
  }

  /** Escape closes the switch dialog first, then the dropdown. */
  onEscape(): void {
    if (this.pendingAccount()) {
      this.cancelSwitch();
      return;
    }
    if (this.isOpen()) {
      this.close();
    }
  }

  // ── Public methods ──────────────────────────────────────────────────────────

  toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen.update((v) => !v);
    if (this.isOpen()) {
      // Lazy: fetch merchant logos only when the switcher is actually opened.
      this.accountLogoStore.hydrate(this.accountStore.accounts());
    } else {
      this.searchQuery.set('');
    }
  }

  /**
   * Pick an account. The active one is a no-op; any other opens the password dialog rather
   * than switching immediately — the switch itself is a re-login that needs the password.
   */
  selectAccount(account: MerchantAccount): void {
    if (this.submitting()) return;
    if (this.isActiveAccount(account)) {
      this.close();
      return;
    }
    this.password.set('');
    this.showPassword.set(false);
    this.dialogError.set(null);
    this.pendingAccount.set(account);
    this.close();
  }

  /** Dismiss the switch dialog without switching. */
  cancelSwitch(): void {
    if (this.submitting()) return;
    this.pendingAccount.set(null);
    this.password.set('');
    this.showPassword.set(false);
    this.dialogError.set(null);
  }

  /** Reveal / hide the password field. */
  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  /**
   * Confirm the switch: re-authenticate against the chosen account, then hard-reload to
   * /wallet so the whole app rebuilds on the fresh session (mirrors the Android switch, which
   * restarts into Home). On failure the dialog stays open with a localized message.
   */
  confirmSwitch(): void {
    const account = this.pendingAccount();
    if (!account || !this.canConfirm()) return;

    this.submitting.set(true);
    this.dialogError.set(null);

    this.authService
      .switchAccount(account.accountId, this.password())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reloadToWallet(),
        error: (error: unknown) => {
          this.submitting.set(false);
          this.dialogError.set(this.toErrorMessage(error));
        },
      });
  }

  isActiveAccount(account: MerchantAccount): boolean {
    return this.accountStore.activeAccount()?.accountId === account.accountId;
  }

  /**
   * Resolved logo URL for an account, or `null` to fall back to the coloured avatar.
   *
   * Priority (locked with product): the payload logo on the account row → the **active**
   * merchant's `logo_url` from `MerchantStore` → the lazily-fetched `AccountLogoStore` cache
   * → none. The `MerchantStore` source is what lets the always-visible trigger show the active
   * account's logo immediately, without opening the dropdown: the auth initializer loads the
   * active merchant's details on every bootstrap, so it is populated app-wide.
   *
   * A relative path is prefixed with the S3 base (`ConfigService.s3BaseUrl()`); an already-
   * absolute URL is used as-is. Returns `null` when there is no logo, the base URL isn't
   * loaded yet, or the image previously failed to load.
   */
  avatarLogo(account: MerchantAccount): string | null {
    if (this.brokenLogos().has(account.accountId)) return null;

    const raw =
      account.logo ||
      this.activeMerchantLogo(account) ||
      this.accountLogoStore.logoFor(account.accountId);
    if (!raw) return null;

    if (/^https?:\/\//i.test(raw)) return raw;

    const base = this.config.s3BaseUrl();
    return base ? base + raw : null;
  }

  /** The active merchant's `logo_url` from MerchantStore, but only for the account it belongs to. */
  private activeMerchantLogo(account: MerchantAccount): string {
    return this.merchantStore.accountId() === account.accountId
      ? (this.merchantStore.merchant()?.logo_url ?? '')
      : '';
  }

  /** A logo image that fails to load (404 / wrong content-type) drops to the colour avatar. */
  onLogoError(account: MerchantAccount): void {
    this.brokenLogos.update((current) => new Set(current).add(account.accountId));
  }

  /**
   * Two-letter avatar initials, falling back to "?" for an account with no name.
   * Shared by the trigger button and the dropdown rows so the two can't diverge.
   */
  initials(account: MerchantAccount): string {
    return (account.name ?? '').slice(0, 2).toUpperCase() || '?';
  }

  /**
   * Returns the full set of Tailwind classes for an account avatar element.
   * Merges static layout classes with the dynamic background colour so the
   * result can be used directly with [class]="getAvatarClass(account)" without
   * needing ngClass.
   *
   * Colour rules: the default merchant is green; other accounts are coloured by
   * role (owner / admin / manager), falling back to green for any other role.
   */
  getAvatarClass(account: MerchantAccount): string {
    const base =
      'flex shrink-0 items-center justify-center rounded-md text-[12px]! font-bold text-white';
    let bg: string;
    if (account.isDefaultMerchant) {
      bg = 'bg-green-500';
    } else {
      switch (account.role) {
        case 'owner':
          bg = 'bg-purple-600';
          break;
        case 'admin':
          bg = 'bg-green-600';
          break;
        case 'manager':
          bg = 'bg-yellow-500';
          break;
        default:
          bg = 'bg-green-500';
      }
    }
    return `${base} ${bg}`;
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  /** Turn a failed re-auth into a localized, actionable message. */
  private toErrorMessage(error: unknown): string {
    const parsed = this.errorHandler.parseError(error as HttpErrorResponse);
    if (parsed.code === ErrorCode.INVALID_PASSWORD) {
      return 'Mot de passe incorrect.';
    }
    return parsed.message || 'Échec du changement de compte. Veuillez réessayer.';
  }

  /** Full reset after a successful switch — reload the app on the new session, landing on /wallet. */
  protected reloadToWallet(): void {
    globalThis.location.assign('/wallet');
  }

  private close(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
  }
}
